import { gql } from "@apollo/client";

/**
 * local state
 */
export const GET_APP_STATE = gql`
  query {
    appState @client
  }
`;

export const GET_MODAL_STATE = gql`
  query {
    modalState @client
  }
`;

/**
 * Delivery Order
 */
export const DeliveryOrderFragments = {
  details: gql`
    fragment DeliveryOrderDetails on DeliveryOrder {
      id
      name
      postalCode
      address
      phoneNumber
      deliveryDate
      deliveryTime {
        code
        section
      }
      deliveryStatus {
        code
      }
      orderClass {
        name
      }
      deliveryProducts {
        product {
          code
          name
          description
          unitSize
        }
        quantity
      }
    }
  `,
};

export const GET_DELIVERY_ORDERS_WITH_KEYWORDS = gql`
  query getDeliveryOrdersWithKeywords(
    $offset: Int
    $limit: Int
    $order: QueryOrderInput
    $keywords: [String!]!
  ) {
    deliveryOrdersWithKeywords(
      offset: $offset
      limit: $limit
      order: $order
      keywords: $keywords
    ) {
      id
      name
      phoneNumber
      postalCode
      address
      deliveryDate
      deliveryTime {
        code
        section
      }
      deliveryStatus {
        code
      }
      deliveryHistory {
        serialNumber
        slipNumber
        product {
          code
          name
        }
      }
      deliveryProducts {
        product {
          code
          name
          description
          unitSize
        }
        quantity
      }
    }
  }
`;

export const GET_DELIVERY_ORDERS = gql`
  query getDeliveryOrder($offset: Int, $limit: Int, $order: QueryOrderInput) {
    deliveryOrders(offset: $offset, limit: $limit, order: $order) {
      id
      name
      phoneNumber
      postalCode
      address
      deliveryDate
      deliveryTime {
        code
        section
      }
      deliveryStatus {
        code
      }
      deliveryHistory {
        serialNumber
        slipNumber
        product {
          code
          name
        }
      }
      deliveryProducts {
        product {
          code
          name
          description
          unitSize
        }
        quantity
      }
    }
  }
`;

export const GET_DELIVERY_ORDER = gql`
  query getDeliveryOrder($id: ID!) {
    deliveryOrder(id: $id) {
      id
      name
      phoneNumber
      postalCode
      address
      deliveryDate
      deliveryTime {
        code
        section
      }
      orderClass {
        name
      }
      deliveryStatus {
        code
      }
      deliveryHistory {
        serialNumber
        slipNumber
        product {
          code
          name
        }
      }
      deliveryProducts {
        product {
          code
          name
          description
          unitSize
        }
        quantity
      }
    }
  }
`;

/**
 * Medical Institution
 */
export const GET_MEDICAL_INSTITUTIONS = gql`
  query GetMedicalInstitutions(
    $skip: Int
    $take: Int
    $order: QueryOrderInput
    $keyword: String
    $withDeleted: Boolean
  ) {
    medicalInstitutions(
      skip: $skip
      take: $take
      order: $order
      keyword: $keyword
      withDeleted: $withDeleted
    ) {
      id
      name
      postalCode
      prefecture
      city
      otherAddress
      phoneNumber
      deletedAt
    }
  }
`;

export const GET_MEDICAL_INSTITUTION = gql`
  query GetMedicalInstitutionDetail($medicalInstitutionId: ID!) {
    medicalInstitutionDetail(medicalInstitutionId: $medicalInstitutionId) {
      id
      name
      phoneNumber
      postalCode
      prefecture
      city
      otherAddress
      isSetupFinished
      deletedAt
      clientCertificate {
        id
        name
        domain
        expiresAt
        downloadUrl
        isUsed
      }
      billingAccount {
        id
        contactName
        contactEmailAddress
        department
        sendEmail
        sendMail
      }
    }
    contracts(medicalInstitutionId: $medicalInstitutionId) {
      id
      initial
      pdt {
        id
        name
        iconPath
      }
      manager {
        id
        name
        department
        emailAddress
        welcomeMailSentAt
      }
      responsiblePerson {
        id
        emailContact {
          id
          name
          department
          emailAddress
        }
        welcomeMailSentAt
      }
      notificationRecipients {
        id
        emailContact {
          id
          name
          department
          emailAddress
        }
      }
    }
    emailContacts: emailContactSummariesByMedicalInstitutionId(
      id: $medicalInstitutionId
    ) {
      id
      name
      department
      emailAddress
      responsiblePersons {
        id
        code
        name
        iconPath
      }
      notificationRecipients {
        id
        code
        name
        iconPath
      }
    }
  }
`;

export const GET_CLIENT_CERTIFICATE = gql`
  query GetClientCertificate($domain: String!) {
    clientCertificate(domain: $domain) {
      id
      password
    }
  }
`;

export const GET_SC_USERS = gql`
  query getScUsers(
    $skip: Int
    $take: Int
    $order: QueryOrderInput
    $keyword: String
  ) {
    scUsers(skip: $skip, take: $take, order: $order, keyword: $keyword) {
      id
      birthday
      firstName
      lastName
      appName
      prescribedAt
      prescriptionCode
      registeredAt
    }
  }
`;

export const GET_SC_USER = gql`
  query getScUser($id: ID!) {
    scUser(id: $id) {
      id
      birthday
      email
      coCheckerSerial
      phoneNumber
      firstName
      lastName
      appName
      prescribedAt
      prescriptionCode
      registeredAt
      medicalInstitutionId
      medicalInstitutionName
      prescription {
        id
        prescriptionCureAppPdtCode {
          status
          statusLabelForCsSystem
        }
      }
    }
  }
`;

export const GET_HT_USERS = gql`
  query getUsers(
    $skip: Int
    $take: Int
    $keyword: String
    $order: QueryOrderInput
  ) {
    htUsers(skip: $skip, take: $take, keyword: $keyword, order: $order) {
      id
      birthday
      nickname
      appName
      prescribedAt
      prescriptionCode
    }
  }
`;

export const GET_HT_USER = gql`
  query getUser($id: ID!) {
    htUser(id: $id) {
      id
      prescriptionCode
      prescribedAt
      status
      nickname
      appName
      pdtCode
      pdtId
      birthday
      medicalInstitutionId
      medicalInstitutionName
      email
      createdAt
      bloodPressureMeasurementRate
      maskedDetail
      prescription {
        id
        name
        prescriptionCureAppPdtCode {
          status
          statusLabelForCsSystem
        }
      }
    }
  }
`;

export const GET_HAUDY_USERS = gql`
  query getHaudyUsers(
    $skip: Int
    $take: Int
    $keyword: String
    $order: QueryOrderInput
  ) {
    haudyUsers(skip: $skip, take: $take, keyword: $keyword, order: $order) {
      id
      prescriptionCode
      prescribedAt
      activatedAt
      status
      nickname
      appName
      pdtCode
      pdtId
      firstConsultationAt
      latestConsultationAt
    }
  }
`;

export const GET_HAUDY_USER = gql`
  query getHaudyUser($id: ID!) {
    haudyUser(id: $id) {
      prescriptionCode
      prescribedAt
      activatedAt
      status
      nickname
      name
      birthMonth
      appName
      pdtCode
      pdtId
      medicalInstitutionId
      medicalInstitutionName
      firstConsultationAt
      latestConsultationAt
      drinkingRecordRate
    }
  }
`;

export const GET_CONTRACTS_INCLUDING_CANCELED = gql`
  query GetContractsIncludingCanceled($medicalInstitutionId: ID!) {
    contractsIncludingCanceled(medicalInstitutionId: $medicalInstitutionId) {
      id
      createdAt
      deletedAt
      pdt {
        id
        name
        iconPath
      }
    }
  }
`;

export const GET_MEDICAL_INSTITUTION_MEMBERS_BY_MEDICAL_INSTITUITON_ID = gql`
  query medicalInstitutionMembersByMedicalInstitutionId(
    $medicalInstitutionId: ID!
  ) {
    medicalInstitutionMembersByMedicalInstitutionId(
      medicalInstitutionId: $medicalInstitutionId
    ) {
      id
      name
      status
      position
      role
      createdAt
      contracts {
        id
        pdt {
          id
          name
          iconPath
        }
      }
    }
  }
`;

/**
 * products
 */
export const GET_PRODUCTS = gql`
  query getProducts {
    products {
      code
      name
      description
    }
  }
`;

/**
 * pdts
 */
export const GET_PDTS = gql`
  query getPdts {
    pdts {
      id
      name
      siteUrl
      officialUrl
      supportUrl
      category
      enableFlag
      hasTentativePrescriptions
      iconPath
      description
      vendor {
        name
      }
      pdtDocuments {
        id
        name
      }
    }
  }
`;

/**
 * pdts
 */
export const GET_PDT = gql`
  query getPdt($id: ID!) {
    pdt(id: $id) {
      id
      name
      siteUrl
      officialUrl
      supportUrl
      category
      enableFlag
      hasTentativePrescriptions
      iconPath
      description
      createdAt
      vendor {
        name
      }
      pdtDocuments {
        id
        name
        url
        version
        enableFlag
        createdAt
      }
    }
  }
`;

/**
 * Prescription
 */
export const GET_PRESCRIPTION_BY_PRESCRIPTION_CODE = gql`
  query getPrescriptionByPrescriptionCode($prescriptionCode: String!) {
    prescriptionByPrescriptionCode(prescriptionCode: $prescriptionCode) {
      id
      name
      birthday
      createdAt
      appName
      medicalInstitutionName
      codeId
      code
      codeStatus
    }
  }
`;

/**
 * Notifications
 */
export const GET_CS_NOTIFICATIONS = gql`
  query GetCsNotifications($input: CsNotificationsInput) {
    csNotifications(input: $input) {
      notifications {
        id
        title
        body
        medicalInstitutionIds
        roles
        positions
        pdtCodes
        termsVersionId
        isEditable
        isDeletable
        startFrom
        endAt
        createdAt
        updatedAt
      }
      totalCount
    }
  }
`;

export const GET_CS_NOTIFICATION = gql`
  query GetCsNotification($id: ID!) {
    csNotification(id: $id) {
      id
      title
      body
      medicalInstitutionIds
      roles
      positions
      pdtCodes
      termsVersionId
      isEditable
      isDeletable
      startFrom
      endAt
      createdAt
      updatedAt
    }
  }
`;

export const PREVIEW_CS_NOTIFICATION_MARKDOWN = gql`
  query PreviewCsNotificationMarkdown($body: String!) {
    previewCsNotificationMarkdown(body: $body)
  }
`;
